import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Select } from './shared';

const PromptLibrary = ({ secretsService }) => {
  const [prompts, setPrompts] = useState([]);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMetaPromptModal, setShowMetaPromptModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [loading, setLoading] = useState(false);

  // API endpoint
  const PROJECTS_API = window.location.protocol === 'https:' ? '/api/projects' : `http://${window.location.hostname}:8010`;

  // Meta-prompt state
  const [metaPromptData, setMetaPromptData] = useState({
    goal: '',
    context: '',
    workflow: 'chat',
    audience: '',
    constraints: '',
    outputFormat: '',
    examples: ''
  });
  const [generatedPrompt, setGeneratedPrompt] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [isLoadingApiKey, setIsLoadingApiKey] = useState(false);
  const [apiKeyError, setApiKeyError] = useState(null);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    tags: [],
    newTag: ''
  });

  // Load prompts from API on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Clear old localStorage data (migration from old version)
    localStorage.removeItem('promptLibrary');
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${PROJECTS_API}/prompts`);
      if (response.ok) {
        const data = await response.json();
        setPrompts(data);
      } else {
        console.error('Failed to load prompts:', response.status);
      }
    } catch (err) {
      console.error('Failed to load prompts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter prompts based on search and tags
  useEffect(() => {
    let filtered = prompts;

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(prompt =>
        prompt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        prompt.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        prompt.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Filter by selected tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter(prompt =>
        selectedTags.every(tag => prompt.tags.includes(tag))
      );
    }

    setFilteredPrompts(filtered);
  }, [prompts, searchQuery, selectedTags]);

  // Get all unique tags from all prompts
  const getAllTags = () => {
    const allTags = new Set();
    prompts.forEach(prompt => {
      prompt.tags.forEach(tag => allTags.add(tag));
    });
    return Array.from(allTags).sort();
  };

  // Extract placeholders from prompt content
  const extractPlaceholders = (content) => {
    const regex = /\{\{([^}]+)\}\}/g;
    const placeholders = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      if (!placeholders.includes(match[1])) {
        placeholders.push(match[1]);
      }
    }
    return placeholders;
  };

  // Replace placeholders with values
  const replacePlaceholders = (content, values) => {
    let result = content;
    Object.keys(values).forEach(key => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      result = result.replace(regex, values[key] || `{{${key}}}`);
    });
    return result;
  };

  // Handle prompt selection
  const handlePromptClick = (prompt) => {
    setSelectedPrompt(prompt);
    const placeholders = extractPlaceholders(prompt.content);
    const initialValues = {};
    placeholders.forEach(ph => {
      initialValues[ph] = '';
    });
    setPlaceholderValues(initialValues);
    setShowPromptModal(true);
  };

  // Handle tag toggle
  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // Handle create/edit prompt
  const handleSavePrompt = async () => {
    if (!formData.name.trim() || !formData.content.trim()) {
      alert('Name and content are required');
      return;
    }

    try {
      const promptData = {
        name: formData.name,
        content: formData.content,
        tags: formData.tags
      };

      if (editingPrompt) {
        // Update existing prompt
        const response = await fetch(`${PROJECTS_API}/prompts/${editingPrompt.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(promptData)
        });

        if (response.ok) {
          await loadPrompts();
          handleCloseCreateModal();
        } else {
          alert('Failed to update prompt');
        }
      } else {
        // Create new prompt
        const response = await fetch(`${PROJECTS_API}/prompts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(promptData)
        });

        if (response.ok) {
          await loadPrompts();
          handleCloseCreateModal();
        } else {
          alert('Failed to create prompt');
        }
      }
    } catch (err) {
      console.error('Failed to save prompt:', err);
      alert('Failed to save prompt');
    }
  };

  // Handle delete prompt
  const handleDeletePrompt = async (promptId) => {
    if (window.confirm('Are you sure you want to delete this prompt?')) {
      try {
        const response = await fetch(`${PROJECTS_API}/prompts/${promptId}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          await loadPrompts();
          if (selectedPrompt?.id === promptId) {
            setShowPromptModal(false);
            setSelectedPrompt(null);
          }
        } else {
          alert('Failed to delete prompt');
        }
      } catch (err) {
        console.error('Failed to delete prompt:', err);
        alert('Failed to delete prompt');
      }
    }
  };

  // Handle open create modal
  const handleOpenCreateModal = (prompt = null) => {
    if (prompt) {
      setEditingPrompt(prompt);
      setFormData({
        name: prompt.name,
        content: prompt.content,
        tags: [...prompt.tags],
        newTag: ''
      });
    } else {
      setEditingPrompt(null);
      setFormData({
        name: '',
        content: '',
        tags: [],
        newTag: ''
      });
    }
    setShowCreateModal(true);
  };

  // Handle close create modal
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setEditingPrompt(null);
    setFormData({
      name: '',
      content: '',
      tags: [],
      newTag: ''
    });
  };

  // Handle add tag to form
  const handleAddTag = () => {
    const tag = formData.newTag.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag],
        newTag: ''
      }));
    }
  };

  // Handle remove tag from form
  const handleRemoveTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy to clipboard');
    });
  };

  // Get vault token for authentication
  const getVaultToken = async () => {
    if (!secretsService) return null;

    try {
      const response = await fetch(`${secretsService.url}/api/v1/auth/service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service_name: 'admin-dashboard',
          api_key: secretsService.apiKey
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.access_token;
      } else {
        console.error('Failed to get vault token:', response.status);
        return null;
      }
    } catch (error) {
      console.error('Failed to authenticate with vault:', error);
      return null;
    }
  };

  // Load OpenAI API key from Secrets service
  const loadOpenAiApiKey = async () => {
    if (!secretsService) return;

    setIsLoadingApiKey(true);
    setApiKeyError(null);

    try {
      const token = await getVaultToken();
      if (!token) {
        setApiKeyError('Failed to authenticate with Secrets service');
        setIsLoadingApiKey(false);
        return;
      }

      const response = await fetch(`${secretsService.url}/api/v1/secrets/shared/OPENAI_API_KEY`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOpenaiApiKey(data.secret_value);
      } else if (response.status === 404) {
        setApiKeyError('OpenAI API key not found in Secrets. Please add "OPENAI_API_KEY" in the Secrets service.');
      } else {
        setApiKeyError('Failed to load OpenAI API key from Secrets');
      }
    } catch (error) {
      console.error('Failed to load OpenAI API key:', error);
      setApiKeyError('Failed to connect to Secrets service');
    } finally {
      setIsLoadingApiKey(false);
    }
  };

  // Load API key when component mounts or secrets service becomes available
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (secretsService) {
      loadOpenAiApiKey();
    }
  }, [secretsService]);

  // Generate prompt using OpenAI
  const generateMetaPrompt = async () => {
    if (!openaiApiKey) {
      alert('OpenAI API key not available. Please add "OPENAI_API_KEY" to the Secrets service.');
      return;
    }

    if (!metaPromptData.goal) {
      alert('Please describe your goal');
      return;
    }

    setIsGenerating(true);

    try {
      const metaPromptTemplate = `Meta-Prompt: Expert Prompt Generator for Multi-Context Use

You are an expert prompt engineer specializing in creating highly effective, workflow-specific prompts. Your task is to generate a comprehensive prompt based on the user's request.

Your Process:

When a user describes what they need, you will:

1. Analyze the Request
   - Understand the user's goal, context, and constraints
   - Identify the workflow (chat assistant, coding agent, API pipeline, RAG system, etc.)
   - Clarify audience (end-user, developer, business team, etc.)
   - Determine the execution environment (one-shot, iterative chat, autonomous agent, backend service)

2. Apply Best Practices
   - Tailor clarity, structure, and constraints to the intended workflow
   - Anticipate risks (ambiguity, hallucination, verbosity, irrelevant reasoning)
   - Align tone and style with audience and purpose

3. Generate a Complete Prompt
   - Create a detailed, well-structured, workflow-specific prompt ready to use
   - Optimize for portability (works across different LLMs/agents if possible)

Best Practices to Apply:

Clarity & Specificity
- Define the AI's role and expertise explicitly
- Specify the exact task and outcome
- Provide concrete instructions and success criteria

Context & Background
- Include purpose and target audience
- Explain how the output will be used (e.g., code injection, chat reply, report section)
- Capture relevant domain background

Workflow Awareness
- Adapt prompt to:
  - Chat assistants → conversational, iterative refinement allowed
  - Coding agents → strict syntax, functional correctness, dry runs/tests
  - Data/RAG systems → reference handling, grounding, citations
  - Automation pipelines → deterministic, structured, machine-readable output

Structure & Format
- Define required output structure (markdown, JSON, code, essay, etc.)
- Specify length, scope, and organization
- Provide schema if output must be parsed by another system

Constraints & Guidelines
- State inclusions/exclusions
- Limit hallucinations (e.g., "If unknown, say so instead of guessing")
- Define tone, style, complexity

Examples & Demonstrations
- Provide positive and negative examples
- Few-shot demonstrations when beneficial

Verification & Robustness
- Request reasoning steps when needed
- Ask model to self-check or summarize constraints
- For code, require comments, docstrings, or tests

Output Template:

Generate a complete, ready-to-use prompt including:

[ROLE DEFINITION] – Who the AI should be
[TASK DESCRIPTION] – The precise goal
[CONTEXT & BACKGROUND] – Situation, audience, workflow, environment
[REQUIREMENTS] – Must-haves, exclusions, constraints
[FORMAT & STRUCTURE] – Output schema, file type, or layout
[WORKFLOW NOTES] – How/where the output will be consumed (chat, code, pipeline, etc.)
[QUALITY CRITERIA] – What defines success
[EXAMPLES] – Good/bad outputs (if relevant)
[ADDITIONAL INSTRUCTIONS] – Special considerations

---

USER REQUEST:

Goal: ${metaPromptData.goal}

Context: ${metaPromptData.context || 'Not specified'}

Workflow Type: ${metaPromptData.workflow}

Target Audience: ${metaPromptData.audience || 'Not specified'}

Constraints: ${metaPromptData.constraints || 'None specified'}

Output Format: ${metaPromptData.outputFormat || 'Not specified'}

Examples: ${metaPromptData.examples || 'None provided'}

---

Please generate a complete, detailed prompt following the best practices above. Return ONLY the generated prompt text, without any meta-commentary or explanation.`;

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${openaiApiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: [
            {
              role: 'user',
              content: metaPromptTemplate
            }
          ],
          temperature: 0.7,
          max_tokens: 2000
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `API request failed: ${response.status}`);
      }

      const data = await response.json();
      const generatedText = data.choices[0].message.content;

      setGeneratedPrompt({
        content: generatedText,
        name: metaPromptData.goal.substring(0, 50) + (metaPromptData.goal.length > 50 ? '...' : ''),
        tags: [metaPromptData.workflow, 'generated']
      });

    } catch (error) {
      console.error('Failed to generate prompt:', error);
      alert(`Failed to generate prompt: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Save generated prompt to library
  const saveGeneratedPrompt = async () => {
    if (!generatedPrompt) return;

    try {
      const promptData = {
        name: generatedPrompt.name,
        content: generatedPrompt.content,
        tags: generatedPrompt.tags
      };

      const response = await fetch(`${PROJECTS_API}/prompts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(promptData)
      });

      if (response.ok) {
        await loadPrompts();

        // Reset meta-prompt modal
        setShowMetaPromptModal(false);
        setGeneratedPrompt(null);
        setMetaPromptData({
          goal: '',
          context: '',
          workflow: 'chat',
          audience: '',
          constraints: '',
          outputFormat: '',
          examples: ''
        });

        alert('Prompt saved to library!');
      } else {
        alert('Failed to save prompt');
      }
    } catch (err) {
      console.error('Failed to save generated prompt:', err);
      alert('Failed to save prompt');
    }
  };

  // Handle meta-prompt modal close
  const handleCloseMetaPromptModal = () => {
    setShowMetaPromptModal(false);
    setGeneratedPrompt(null);
    setMetaPromptData({
      goal: '',
      context: '',
      workflow: 'chat',
      audience: '',
      constraints: '',
      outputFormat: '',
      examples: ''
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI Prompt Library</h1>
            <p className="text-gray-600 text-sm mt-1">
              Manage and use reusable AI prompts with dynamic placeholders
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowMetaPromptModal(true)}
              variant="primary"
              title="Generate prompt using AI"
            >
              <i className="fas fa-magic mr-2"></i>
              AI Generator
            </Button>
            <Button
              onClick={() => handleOpenCreateModal()}
              variant="primary"
            >
              <i className="fas fa-plus mr-2"></i>
              New Prompt
            </Button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="space-y-3">
          <Input
            type="text"
            placeholder="Search prompts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
          />

          {/* Tag Filter */}
          <div className="flex flex-wrap gap-2">
            {getAllTags().map(tag => (
              <Button
                key={tag}
                onClick={() => toggleTag(tag)}
                variant={selectedTags.includes(tag) ? 'primary' : 'secondary'}
                size="sm"
              >
                {tag}
              </Button>
            ))}
            {selectedTags.length > 0 && (
              <Button
                onClick={() => setSelectedTags([])}
                variant="danger"
                size="sm"
              >
                Clear filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Prompt Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-5xl mb-4">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <p className="text-gray-600">Loading prompts...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPrompts.map(prompt => (
          <div
            key={prompt.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="flex items-start justify-between mb-2">
              <h3
                onClick={() => handlePromptClick(prompt)}
                className="text-lg font-semibold text-gray-900 hover:text-[#FFC65D] flex-1"
              >
                {prompt.name}
              </h3>
              <div className="flex gap-1 ml-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(prompt.content);
                  }}
                  className="p-1.5 text-gray-400 hover:text-green-600 transition-colors"
                  title="Copy prompt"
                >
                  <i className="fas fa-copy text-sm"></i>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenCreateModal(prompt);
                  }}
                  className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Edit"
                >
                  <i className="fas fa-edit text-sm"></i>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePrompt(prompt.id);
                  }}
                  className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                  title="Delete"
                >
                  <i className="fas fa-trash text-sm"></i>
                </button>
              </div>
            </div>
            <p
              onClick={() => handlePromptClick(prompt)}
              className="text-gray-600 text-sm mb-3 line-clamp-3"
            >
              {prompt.content.substring(0, 150)}...
            </p>
            <div className="flex flex-wrap gap-1">
              {prompt.tags.map(tag => (
                <span
                  key={tag}
                  className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        ))}
        </div>
      )}

      {!loading && filteredPrompts.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-5xl mb-4">
            <i className="fas fa-search"></i>
          </div>
          <p className="text-gray-600">No prompts found</p>
          <p className="text-gray-500 text-sm mt-1">
            {searchQuery || selectedTags.length > 0
              ? 'Try adjusting your search or filters'
              : 'Create your first prompt to get started'}
          </p>
        </div>
      )}

      {/* Prompt Detail Modal */}
      <Modal
        isOpen={showPromptModal}
        onClose={() => {
          setShowPromptModal(false);
          setSelectedPrompt(null);
          setPlaceholderValues({});
        }}
        title={selectedPrompt?.name}
        size="lg"
      >
        {selectedPrompt && (
          <>
            {/* Tags */}
            <div className="flex flex-wrap gap-1 mb-4">
              {selectedPrompt.tags.map(tag => (
                <span
                  key={tag}
                  className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Placeholder Inputs */}
            {extractPlaceholders(selectedPrompt.content).length > 0 && (
              <div className="mb-6 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Fill in placeholders:</h3>
                {extractPlaceholders(selectedPrompt.content).map(placeholder => (
                  <Input
                    key={placeholder}
                    type="textarea"
                    label={placeholder.replace(/_/g, ' ')}
                    value={placeholderValues[placeholder] || ''}
                    onChange={(e) =>
                      setPlaceholderValues(prev => ({
                        ...prev,
                        [placeholder]: e.target.value
                      }))
                    }
                    rows={2}
                    placeholder={`Enter ${placeholder.replace(/_/g, ' ')}`}
                    fullWidth
                  />
                ))}
              </div>
            )}

            {/* Preview */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900">Preview:</h3>
                <Button
                  onClick={() => copyToClipboard(replacePlaceholders(selectedPrompt.content, placeholderValues))}
                  variant="secondary"
                  size="sm"
                >
                  <i className="fas fa-copy mr-1"></i>
                  Copy
                </Button>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 whitespace-pre-wrap text-sm font-mono">
                {replacePlaceholders(selectedPrompt.content, placeholderValues)}
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={handleCloseCreateModal}
        title={editingPrompt ? 'Edit Prompt' : 'Create New Prompt'}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={handleCloseCreateModal}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePrompt}
              variant="primary"
            >
              {editingPrompt ? 'Save Changes' : 'Create Prompt'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Name */}
          <Input
            type="text"
            label="Prompt Name *"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Code Review Request"
            fullWidth
          />

          {/* Content */}
          <div>
            <Input
              type="textarea"
              label="Prompt Content *"
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              rows={10}
              placeholder="Enter your prompt with {{placeholders}}"
              fullWidth
            />
            <p className="text-xs text-gray-500 mt-1">
              Use <code className="bg-gray-100 px-1 rounded">{'{{placeholder_name}}'}</code> for dynamic fields
            </p>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <div className="flex gap-2 mb-2">
              <Input
                type="text"
                value={formData.newTag}
                onChange={(e) => setFormData(prev => ({ ...prev, newTag: e.target.value }))}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                placeholder="Add a tag"
                fullWidth
              />
              <Button
                onClick={handleAddTag}
                variant="secondary"
              >
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map(tag => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-[#FFC65D] text-black rounded-full text-sm flex items-center gap-2"
                >
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:text-red-600"
                  >
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* Meta-Prompt Generator Modal */}
      <Modal
        isOpen={showMetaPromptModal}
        onClose={handleCloseMetaPromptModal}
        title={
          <div>
            <h2 className="text-xl font-semibold text-gray-900">AI Prompt Generator</h2>
            <p className="text-sm text-gray-600 mt-1">Generate professional prompts using AI</p>
          </div>
        }
        size="xl"
        footer={
          !generatedPrompt ? (
            <div className="flex justify-end gap-2">
              <Button
                onClick={handleCloseMetaPromptModal}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={generateMetaPrompt}
                disabled={isGenerating || !openaiApiKey || !metaPromptData.goal}
                variant="primary"
              >
                {isGenerating ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-magic mr-2"></i>
                    Generate Prompt
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="flex justify-between">
              <Button
                onClick={() => setGeneratedPrompt(null)}
                variant="secondary"
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Back
              </Button>
              <div className="flex gap-2">
                <Button
                  onClick={handleCloseMetaPromptModal}
                  variant="secondary"
                >
                  Discard
                </Button>
                <Button
                  onClick={saveGeneratedPrompt}
                  variant="primary"
                >
                  <i className="fas fa-save mr-2"></i>
                  Save to Library
                </Button>
              </div>
            </div>
          )
        }
      >
        {!generatedPrompt ? (
          <div className="space-y-4">
            {/* OpenAI API Key Status */}
            <div className={`rounded-lg p-4 ${
              isLoadingApiKey
                ? 'bg-gray-50 border border-gray-200'
                : apiKeyError
                ? 'bg-red-50 border border-red-200'
                : openaiApiKey
                ? 'bg-green-50 border border-green-200'
                : 'bg-yellow-50 border border-yellow-200'
            }`}>
              <div className="flex items-start gap-3">
                {isLoadingApiKey ? (
                  <i className="fas fa-spinner fa-spin text-gray-600 mt-1"></i>
                ) : apiKeyError ? (
                  <i className="fas fa-exclamation-triangle text-red-600 mt-1"></i>
                ) : openaiApiKey ? (
                  <i className="fas fa-check-circle text-green-600 mt-1"></i>
                ) : (
                  <i className="fas fa-key text-yellow-600 mt-1"></i>
                )}
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">
                    {isLoadingApiKey
                      ? 'Loading API Key...'
                      : apiKeyError
                      ? 'API Key Not Found'
                      : openaiApiKey
                      ? 'API Key Loaded'
                      : 'API Key Required'}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {isLoadingApiKey
                      ? 'Fetching OpenAI API key from Secrets service...'
                      : apiKeyError
                      ? apiKeyError
                      : openaiApiKey
                      ? 'OpenAI API key loaded from Secrets service (OPENAI_API_KEY)'
                      : 'Add "OPENAI_API_KEY" to the Secrets service to use this feature'}
                  </p>
                  {apiKeyError && (
                    <Button
                      onClick={loadOpenAiApiKey}
                      variant="danger"
                      size="sm"
                      className="mt-2"
                    >
                      <i className="fas fa-redo mr-1"></i>
                      Retry
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Goal */}
            <Input
              type="textarea"
              label={<>Goal / Purpose * <span className="text-gray-500 font-normal">(What do you want to achieve?)</span></>}
              value={metaPromptData.goal}
              onChange={(e) => setMetaPromptData(prev => ({ ...prev, goal: e.target.value }))}
              rows={3}
              placeholder="e.g., Create a prompt that helps analyze code for security vulnerabilities"
              fullWidth
            />

            {/* Context */}
            <Input
              type="textarea"
              label="Context / Background"
              value={metaPromptData.context}
              onChange={(e) => setMetaPromptData(prev => ({ ...prev, context: e.target.value }))}
              rows={2}
              placeholder="Describe the situation, domain, or background information"
              fullWidth
            />

            {/* Workflow Type */}
            <Select
              label="Workflow Type"
              value={metaPromptData.workflow}
              onChange={(e) => setMetaPromptData(prev => ({ ...prev, workflow: e.target.value }))}
              options={[
                { value: 'chat', label: 'Chat Assistant' },
                { value: 'coding', label: 'Coding Agent' },
                { value: 'api', label: 'API/Pipeline' },
                { value: 'rag', label: 'RAG System' },
                { value: 'automation', label: 'Automation' },
                { value: 'analysis', label: 'Analysis/Research' },
                { value: 'writing', label: 'Content Writing' }
              ]}
              fullWidth
            />

            {/* Audience */}
            <Input
              type="text"
              label="Target Audience"
              value={metaPromptData.audience}
              onChange={(e) => setMetaPromptData(prev => ({ ...prev, audience: e.target.value }))}
              placeholder="e.g., developers, business analysts, end users"
              fullWidth
            />

            {/* Constraints */}
            <Input
              type="textarea"
              label="Constraints / Requirements"
              value={metaPromptData.constraints}
              onChange={(e) => setMetaPromptData(prev => ({ ...prev, constraints: e.target.value }))}
              rows={2}
              placeholder="Any limitations, must-haves, or exclusions"
              fullWidth
            />

            {/* Output Format */}
            <Input
              type="text"
              label="Output Format"
              value={metaPromptData.outputFormat}
              onChange={(e) => setMetaPromptData(prev => ({ ...prev, outputFormat: e.target.value }))}
              placeholder="e.g., JSON, markdown, code, essay"
              fullWidth
            />

            {/* Examples */}
            <Input
              type="textarea"
              label="Examples (optional)"
              value={metaPromptData.examples}
              onChange={(e) => setMetaPromptData(prev => ({ ...prev, examples: e.target.value }))}
              rows={3}
              placeholder="Provide example inputs/outputs if helpful"
              fullWidth
            />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Generated Prompt Preview */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900">Generated Prompt:</h3>
                <Button
                  onClick={() => copyToClipboard(generatedPrompt.content)}
                  variant="secondary"
                  size="sm"
                >
                  <i className="fas fa-copy mr-1"></i>
                  Copy
                </Button>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto whitespace-pre-wrap text-sm">
                {generatedPrompt.content}
              </div>
            </div>

            {/* Prompt Name */}
            <Input
              type="text"
              label="Prompt Name"
              value={generatedPrompt.name}
              onChange={(e) => setGeneratedPrompt(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
            />

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {generatedPrompt.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center gap-2"
                  >
                    {tag}
                    <button
                      onClick={() => setGeneratedPrompt(prev => ({
                        ...prev,
                        tags: prev.tags.filter((_, i) => i !== idx)
                      }))}
                      className="hover:text-red-600"
                    >
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  </span>
                ))}
                <button
                  onClick={() => {
                    const newTag = prompt('Enter tag name:');
                    if (newTag) {
                      setGeneratedPrompt(prev => ({
                        ...prev,
                        tags: [...prev.tags, newTag.toLowerCase()]
                      }));
                    }
                  }}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200"
                >
                  <i className="fas fa-plus mr-1"></i>
                  Add Tag
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PromptLibrary;
